/**
 * Seata wizard TX for cross-service working paper save (audit + account).
 *
 * Shared by all WP_CROSS_SERVICE_CODES (BS-EQ-IC / BS-CA-CASH / BS-CL-BOD / BS-EQ-DIV /
 * PL-TURN-SI / PL-TURN-SE / PL-TAX) via saveOrUpdate and component-level batch wraps.
 */
import {BASE_AUDIT_API} from '@/services/api';
import {SEATA_TX_NAMES} from '@/services/seataGlobalTxConstants';
import {
    beginWizardTx,
    commitWizardTx,
    rollbackWizardTx,
} from '@/services/seataGlobalTxService';
import {SEATA_WIZARD_FLOW, setActiveSeataFlow, setSeataTxXid} from '@/services/seataTxHolder';

const FLOW = SEATA_WIZARD_FLOW.WP_SAVE;
const API_BASE = BASE_AUDIT_API;
const TX_NAME = SEATA_TX_NAMES.WP_SAVE;

/** Serialize top-level WP Seata saves so concurrent idle/manual saves cannot overwrite holder xid. */
let wpSaveTail: Promise<void> = Promise.resolve();

/** Nesting depth within one top-level run (join outer TX instead of begin again). */
let wpSaveDepth = 0;
let wpSaveActiveXid: string | null = null;

export function activateWpSaveSeataFlow(): void {
    setActiveSeataFlow(FLOW);
}

export async function runWpSaveWithGlobalTx<T>(save: () => Promise<T>): Promise<T> {
    // Nested call (outer already holds TX): join — do not begin/commit again.
    if (wpSaveDepth > 0 && wpSaveActiveXid) {
        setActiveSeataFlow(FLOW);
        setSeataTxXid(FLOW, wpSaveActiveXid);
        return save();
    }

    let release!: () => void;
    const gate = new Promise<void>((resolve) => {
        release = resolve;
    });
    const prev = wpSaveTail;
    wpSaveTail = prev.then(() => gate);
    await prev;

    // WHY: pin begin xid — holder can be overwritten by a concurrent WP save (idle autosave /
    // nested runWpSaveWithGlobalTx). commitWizardTx without xid silently no-ops, leaving the
    // AT branch uncommitted until WIZARD_TX_TIMEOUT (30m) undo — IC transfers vanish from DB.
    const xid = await beginWizardTx(FLOW, API_BASE, TX_NAME);
    wpSaveDepth += 1;
    wpSaveActiveXid = xid;
    try {
        const result = await save();
        await commitWizardTx(FLOW, API_BASE, xid);
        return result;
    } catch (error) {
        try {
            await rollbackWizardTx(FLOW, API_BASE, xid);
        } catch (rollbackError) {
            console.error('Failed to rollback WP save global transaction', rollbackError);
        }
        throw error;
    } finally {
        wpSaveDepth = Math.max(0, wpSaveDepth - 1);
        wpSaveActiveXid = null;
        release();
    }
}
